import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const { toast } = useToast();
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    // Restore session on mount
    useEffect(() => {
        const restoreSession = async () => {
            const token = localStorage.getItem('provatio_token');
            const savedUser = localStorage.getItem('provatio_user');

            if (token && savedUser) {
                try {
                    const userData = await api.auth.me();
                    setUser(userData);
                    setSession({ user: userData, access_token: token });
                } catch {
                    // Token expired or invalid
                    api.clearToken();
                    setUser(null);
                    setSession(null);
                }
            }
            setLoading(false);
        };
        restoreSession();
    }, []);

    // Listen for auth expired events
    useEffect(() => {
        const handleExpired = () => {
            setUser(null);
            setSession(null);
            toast({
                variant: "destructive",
                title: "Sessão expirada",
                description: "Sua sessão expirou. Faça login novamente."
            });
        };
        window.addEventListener('auth:expired', handleExpired);
        return () => window.removeEventListener('auth:expired', handleExpired);
    }, [toast]);

    const login = useCallback(async (email, password) => {
        try {
            const data = await api.auth.login(email, password);
            if (data.user) {
                setUser(data.user);
                setSession({ user: data.user, access_token: data.token });
                return { user: data.user, error: null };
            }
            return { error: { message: 'Resposta inesperada do servidor' } };
        } catch (err) {
            const message = err.error || 'Credenciais inválidas';
            toast({
                variant: "destructive",
                title: "Erro no Login",
                description: message
            });
            return { error: { message } };
        }
    }, [toast]);

    const signup = useCallback(async (email, password) => {
        // Registration handled by admin — show info
        toast({
            title: "Cadastro não disponível",
            description: "Solicite acesso ao administrador do sistema."
        });
        return { user: null, error: { message: 'Cadastro realizado apenas por administradores' } };
    }, [toast]);

    const logout = useCallback(async () => {
        await api.auth.logout();
        setUser(null);
        setSession(null);
    }, []);

    const value = useMemo(() => ({
        user,
        session,
        loading,
        login,
        signup,
        logout,
        isAuthenticated: !!user,
    }), [user, session, loading, login, signup, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};